// @ts-strict-ignore
import { useState, useCallback } from 'react'
import { IAPIError } from 'types'
import { getErrorText } from 'services/error'
import { trackError } from 'services/analytics'
import { isIOS } from 'services/browserAgent'
import { openLocalFile } from 'services/files'
import { graphql } from 'react-relay'
import { video_CreateVideoMutation } from '__generated__/video_CreateVideoMutation.graphql'
import {
  VideoStatusEnum,
  video_CompleteVideoUploadMutation,
} from '__generated__/video_CompleteVideoUploadMutation.graphql'
import { useSnackbar } from './redux'
import { useQuickCommit } from './useQuickCommit'

export type IUploadedVideo = {
  id: string
  posterUrl?: string
  rawId?: number
  status?: VideoStatusEnum
  url?: string
}

export type IUploadedVideoWQuestionData = {
  id: string
  posterUrl?: string
  rawId?: number
  status?: VideoStatusEnum
  url?: string
  question?: {
    id: string
    title: string
  } | null
}

export interface IUseVideoSaveOptions {
  videoParams?: Omit<video_CreateVideoMutation['variables']['input'], 'extension'>
  onUploadCompleted?: (video: IUploadedVideo) => void
  onUploadFailed?: (error: IAPIError) => void
}

export interface IUseVideoRecorderOptions extends IUseVideoSaveOptions {
  preferNativeRecorderOnIOS?: boolean
  onOpen?: () => void
  onClose?: () => void
}

async function putVideoToS3(url: string, file: File, onProgress: (progress: number) => void) {
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.open('PUT', url, true)
    xhr.onload = () => resolve(xhr.responseText)
    xhr.onabort = () => reject(new Error('Request aborted.'))
    xhr.onerror = () => reject(new Error(xhr.statusText))

    if (onProgress) {
      xhr.upload.onprogress = e => e.lengthComputable ? onProgress(e.loaded / e.total) : undefined
    }

    xhr.send(file)
  })
}

export async function storeVideoS3(url: string, file: File, onProgress: (progress: number) => void): Promise<void> {
  const MAX_RETRIES = 3
  let retryAttempts = 0

  do {
    try {
      await putVideoToS3(url, file, onProgress)
      break
    } catch (err) {
      if (++retryAttempts < MAX_RETRIES) {
        trackError(err)
      } else {
        throw err
      }
    }
  } while (retryAttempts < MAX_RETRIES)
}

/**
 * React hook that sets up storage handling for video uploads
 */
export function useVideoSave(options: IUseVideoSaveOptions = {}) {
  const { onUploadCompleted, onUploadFailed, videoParams = {} } = options

  const [error, setError] = useState<Error>(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)

  const { showSnackbarMessage } = useSnackbar()

  const { execute: createVideo, error: createVideoError } = useQuickCommit<video_CreateVideoMutation>(graphql`
    mutation video_CreateVideoMutation($input: CreateVideoInput!) {
      createVideo(input: $input) {
        uploadUrl
        video {
          id
          status
          question {
            # Refresh AnswerListItem
            ...AnswerListItem_Question
            ...ScreeningQuestionStep_Question
          }
          
          user {
            # Make sure Relay knows if the user now has a video
            video {
              ...AnswerListItem_Video
              ...ScreeningQuestionStep_Video
            }

            ...ScreeningQuestionStep_User
          }
          
          contractRequests {
            status

            jobApplication {
              # In case the user submits a video before they click accept, the backend will auto accept the requests
              # We refresh the value here to update the frontend
              status
            }
          }

          ...ScreeningQuestionStep_Video
        }
      }
    }
  `)

  const { execute: completeVideoUpload, error: completeVideoUploadError } = useQuickCommit<video_CompleteVideoUploadMutation>(graphql`
    mutation video_CompleteVideoUploadMutation($input: CompleteVideoUploadInput!) {
      completeVideoUpload(input: $input) {
        video {
          id
          url
          status
          rawId
          posterUrl
          question {
            ...AnswerListItem_Question
          }
        }
      }
    }
  `)

  const saveVideo = useCallback(async (file: File) => {
    setUploadProgress(0)
    setError(null)
    setIsUploading(true)

    try {
      // create pending Video, and get the presigned upload URL for the actual file
      const createVideoResult = await createVideo({
        input: {
          extension: file.name?.split('.')?.pop(),
          ...videoParams,
        },
      })

      if (createVideoResult?.createVideo) {
        // perform the actual upload to S3
        await storeVideoS3(createVideoResult.createVideo.uploadUrl, file, setUploadProgress)

        // signal video upload completion to the backend, so that it can start handling the video (conversion, etc)
        const completeVideoUploadResult = await completeVideoUpload({
          input: {
            videoId: createVideoResult.createVideo.video.id,
          },
        })

        const completedVideo = completeVideoUploadResult?.completeVideoUpload.video

        if (completedVideo && typeof onUploadCompleted === 'function') {
          onUploadCompleted(completedVideo)
        }

        setUploadProgress(1)
      }
    } catch (err) {
      setError(err)
      trackError(err)
      showSnackbarMessage(getErrorText(err), { level: 'error' })

      if (typeof onUploadFailed === 'function') {
        onUploadFailed(err)
      }
    }

    setIsUploading(false)
  }, [setIsUploading, videoParams, onUploadCompleted, onUploadFailed, completeVideoUpload, createVideo, showSnackbarMessage])

  return {
    saveVideo: saveVideo,
    error: error || createVideoError || completeVideoUploadError,
    isUploading: isUploading,
    uploadProgress: uploadProgress,
  }
}

/**
 * React hook that sets up props and storage handling for video recorders and uploading.
 */
export function useVideoRecorder(options: IUseVideoRecorderOptions = {}) {
  options = {
    preferNativeRecorderOnIOS: true,
    ...options,
  }

  const [isVideoRecorderOpen, setIsVideoRecorderOpen] = useState(false)
  const { error, isUploading, saveVideo, uploadProgress } = useVideoSave(options)
  const { onOpen, onClose } = options

  const handleClose = useCallback(() => {
    setIsVideoRecorderOpen(false)

    if (onClose) {
      onClose()
    }
  }, [setIsVideoRecorderOpen, onClose])

  const handleVideoAvailable = useCallback(async (file: File) => {
    setIsVideoRecorderOpen(false)
    saveVideo(file)
  }, [saveVideo])

  const openVideoRecorder = useCallback(async () => {
    if (options.preferNativeRecorderOnIOS && isIOS()) {
      // essentially every modern mobile device has a built-in recorder app, available when browsing for files
      // it's better UX to use that instead of showing the custom video overlay
      const file = await openLocalFile(['video/mp4', 'video/x-m4v', 'video/*'], 'camera')

      if (file) {
        handleVideoAvailable(file)
      }
    } else {
      setIsVideoRecorderOpen(true)

      if (onOpen) {
        onOpen()
      }
    }
  }, [setIsVideoRecorderOpen, handleVideoAvailable, onOpen, options.preferNativeRecorderOnIOS])

  return {
    isUploading,
    uploadProgress,
    isVideoRecorderOpen,
    error: error,
    openVideoRecorder,
    saveOtherVideo: saveVideo,
    videoRecorderProps: {
      onVideoAvailable: handleVideoAvailable,
      onClose: handleClose,
    },
  }
}
